import Layout from "@/components/Layout";
import MetaTags from "@/components/SEO/MetaTags";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Sparkles, Heart, Users, Award } from "lucide-react";
import { trackEvent } from "@/utils/analytics";

const About = () => {
  const values = [
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: "Innovation",
      description: "We blend cutting-edge AI technology with personalized aesthetic care to create transformative experiences."
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: "Compassion",
      description: "Every treatment is delivered with genuine care, ensuring you feel comfortable and confident throughout your journey."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Community",
      description: "We're building a community where beauty, wellness, and self-confidence flourish together."
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: "Excellence",
      description: "Our commitment to the highest standards of care ensures exceptional results for every patient."
    }
  ];

  const handleJoinJourney = () => {
    trackEvent({
      action: 'click_join_journey',
      category: 'engagement',
      label: 'about_page'
    });
  };

  return (
    <>
      <MetaTags 
        title="About JUV Medspa™ - Our Story & Mission"
        description="Learn about JUV Medspa™'s mission to democratize aesthetic healthcare through innovation, expertise, and genuine care. Founded by Dr. Parth Patel and Jonathan Cao."
        keywords={['about juv', 'medspa story', 'aesthetic medicine mission', 'dr parth patel', 'jonathan cao']}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          'mainEntity': {
            '@type': 'MedicalBusiness',
            'name': 'JUV Medspa™'
          }
        }}
      />
      <Layout>
        <section className="py-20 bg-gradient-to-br from-juv-cream-white to-white">
          <div className="container mx-auto px-6">
            {/* Hero Section */}
            <div className="text-center max-w-4xl mx-auto mb-20">
              <h1 className="text-4xl md:text-6xl font-bold text-juv-deep-chestnut mb-6 font-juv-display">
                About JUV
              </h1>
              <p className="text-xl text-juv-deep-chestnut/70 leading-relaxed mb-8">
                Transforming the future of aesthetic healthcare through innovation, expertise, and genuine care.
              </p>
              <div className="w-24 h-1 bg-juv-gradient-primary mx-auto rounded-full"></div>
            </div>

            {/* Story Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-20">
              <div className="space-y-6">
                <h2 className="text-3xl font-bold text-juv-deep-chestnut font-juv-display">
                  Our Story
                </h2>
                <p className="text-lg text-juv-deep-chestnut/70 leading-relaxed">
                  JUV was born from a simple belief: everyone deserves to feel confident in their own skin. 
                  Founded by Dr. Parth Patel and Jonathan Cao, we recognized the gap between traditional 
                  dental practices and the growing demand for comprehensive aesthetic care.
                </p>
                <p className="text-lg text-juv-deep-chestnut/70 leading-relaxed">
                  By combining Dr. Patel's clinical expertise with Jonathan's AI and technology background, 
                  we've created the first platform to seamlessly integrate dental offices with medspa services, 
                  making aesthetic treatments more accessible and personalized than ever before.
                </p>
              </div>
              <div className="bg-gradient-to-br from-juv-soft-blush/30 to-juv-rose-clay/20 rounded-2xl p-8 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-6xl mb-4">✨</div>
                  <h3 className="text-2xl font-bold text-juv-deep-chestnut mb-2">Our Mission</h3>
                  <p className="text-juv-deep-chestnut/70">
                    To democratize aesthetic healthcare through technology, making personalized beauty and wellness accessible to all.
                  </p>
                </div>
              </div>
            </div>

            {/* Values Section */}
            <div className="mb-20">
              <h2 className="text-3xl font-bold text-juv-deep-chestnut text-center mb-12 font-juv-display">
                Our Values
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {values.map((value, index) => (
                  <Card key={index} className="bg-white/50 border-juv-rose-clay/20 hover:shadow-luxury transition-all duration-300">
                    <CardContent className="p-6 text-center">
                      <div className="w-16 h-16 bg-juv-gradient-primary rounded-full flex items-center justify-center mx-auto mb-4 text-white">
                        {value.icon}
                      </div>
                      <h3 className="text-xl font-bold text-juv-deep-chestnut mb-3 font-juv-heading">
                        {value.title}
                      </h3>
                      <p className="text-juv-deep-chestnut/70 leading-relaxed">
                        {value.description}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Vision Section */}
            <div className="bg-gradient-to-r from-juv-terracotta to-juv-deep-chestnut rounded-3xl p-12 text-center text-white">
              <h2 className="text-3xl font-bold mb-6 font-juv-display">
                Our Vision for the Future
              </h2>
              <p className="text-xl mb-8 max-w-3xl mx-auto leading-relaxed opacity-90">
                We envision a world where aesthetic healthcare is personalized, accessible, and seamlessly integrated 
                into your wellness journey. Through AI-powered insights and expert care, we're building the future 
                of beauty and confidence.
              </p>
              <Button 
                className="bg-white text-juv-deep-chestnut hover:bg-juv-cream-white"
                onClick={handleJoinJourney}
              >
                Join Our Journey
              </Button>
            </div>
          </div>
        </section>
      </Layout>
    </>
  );
};

export default About;
